import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
} from "@/components/ui/dropdown-menu";
import { api } from "@/lib/api";

function timeAgo(iso) {
  if (!iso) return "";
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return d < 7 ? `${d}d ago` : new Date(iso).toLocaleDateString();
}

export function NotificationsBell() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/notifications");
      setItems(r.data?.items || []);
      setUnread(r.data?.unread || 0);
    } catch { /* not signed in yet / offline — ignore */ }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 30000);
    const onSignal = () => load();
    window.addEventListener("focus", onSignal);
    window.addEventListener("ubos:notifications-changed", onSignal);
    return () => {
      clearInterval(id);
      window.removeEventListener("focus", onSignal);
      window.removeEventListener("ubos:notifications-changed", onSignal);
    };
  }, [load]);

  const markAll = async () => {
    try { await api.post("/notifications/read", { all: true }); } catch { /* ignore */ }
    load();
  };

  const openItem = async (n) => {
    if (!n.read) { try { await api.post("/notifications/read", { ids: [n.id] }); } catch { /* ignore */ } }
    setOpen(false);
    if (n.ref?.record_id) nav(`/records/${n.ref.record_id}`);
    load();
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost" size="icon"
          className="relative h-9 w-9 text-muted-foreground"
          data-testid="notifications-btn"
          aria-label={unread > 0 ? `Notifications (${unread} unread)` : "Notifications"}
        >
          <Bell className="w-4 h-4" />
          {unread > 0 && (
            <span
              className="absolute top-1 right-1 min-w-[16px] h-4 px-1 rounded-full bg-primary text-primary-foreground text-[10px] font-semibold flex items-center justify-center"
              data-testid="notifications-badge"
            >
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[340px] p-0" data-testid="notifications-menu">
        <div className="flex items-center justify-between px-3 py-2 border-b border-border">
          <span className="text-sm font-semibold">Notifications</span>
          {unread > 0 && (
            <button onClick={markAll} className="text-xs text-primary hover:underline" data-testid="notifications-mark-all">
              Mark all read
            </button>
          )}
        </div>
        <div className="max-h-[380px] overflow-y-auto">
          {items.length === 0 ? (
            <div className="px-3 py-10 text-center text-sm text-muted-foreground">You're all caught up.</div>
          ) : (
            items.map((n) => (
              <button
                key={n.id}
                onClick={() => openItem(n)}
                className={`w-full text-left px-3 py-2.5 border-b border-border/60 hover:bg-muted flex gap-2 transition-colors ${n.read ? "" : "bg-primary/5"}`}
                data-testid={`notification-${n.id}`}
              >
                <span className={`mt-1.5 w-1.5 h-1.5 rounded-full shrink-0 ${n.read ? "bg-transparent" : "bg-primary"}`} />
                <span className="min-w-0 flex-1">
                  <span className="block text-sm leading-snug break-words">{n.title}</span>
                  <span className="block text-[11px] text-muted-foreground mt-0.5">{timeAgo(n.created_at)}</span>
                </span>
              </button>
            ))
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
